// ─────────────────────────────────────────────────────────────────────────────
// WP-F3 / CPE-158 — la COMPOSICIÓN de «Vistas de Usuario» con las reglas que ya existían.
//
// 🔴 Este fichero es la decisión del paquete, no un envoltorio. Al medir (2026-08-10) se encontró que
// SEIS de las doce vistas ya tenían un interruptor vivo por otro camino. Había dos salidas:
//
//   (a) que `UserViewSettings` fuera la única verdad → seis pares de mecanismos compitiendo por la
//       misma pregunta, que es exactamente cómo aparecen los defectos de «se aprobó y no hizo nada»;
//   (b) COMPONER: la vista se muestra si la OU la ofrece **y** su propia regla lo permite.
//
// Se eligió (b) porque las preguntas son distintas de verdad: el kill-switch dice «el programa está
// en pausa», el anti-spoiler «hoy no, que hay premiación», y esto «aquí no se ofrece». Y se compone
// AQUÍ, en un sitio, para que ningún consumidor tenga que acordarse de las dos mitades.
// ─────────────────────────────────────────────────────────────────────────────
import { useUserViews, useRecognitionRules } from '@/api/recognition';
import type { UserViewKey } from '@/api/recognition';
import { USER_VIEWS_DEFAULT } from '@/api/recognition';
import { useCurrentUser } from '@/hooks/useCurrentUser';

/**
 * ¿Se ofrece esta vista al usuario actual?
 *
 * Mientras la configuración carga —o si falla— se devuelve el DEFAULT de la maqueta, es decir se
 * falla ABIERTO. Es deliberado y al revés que la mayoría de gates del módulo: aquí no se protege un
 * dato, se decide una interfaz. Fallar cerrado dejaría la aplicación medio vacía en cada carga y en
 * cada error de red, y quien esté detrás de un gate de verdad sigue estándolo — porque estas vistas
 * se COMPONEN con esas reglas en vez de sustituirlas.
 */
export function useVistaHabilitada(clave: UserViewKey, programa?: string | null): boolean {
  // Bug (2026-08-21) — sin `ouId`, `useUserViews` siempre resolvía el documento GLOBAL: el backend
  // (`UserViewSettingsRepository.GetEffectiveAsync`) solo busca el documento de OU cuando recibe un
  // `ouId`, y ningún llamador se lo mandaba. Apagar una vista para una OU específica (p. ej. «Ver a
  // tu equipo» en MEXICO) quedaba guardado en Mongo pero sin ningún efecto en pantalla, en las 12
  // vistas y en todas las OUs. `ouId` es el mismo campo que ya usan campaña activa/GDPR/reportería.
  const ouId = useCurrentUser()?.ouId;
  const vistas = useUserViews(ouId);
  // `programa` resuelve las reglas EFECTIVAS del programa correcto (Brillamos lo pasa explícito): sin
  // él, el kill-switch `sendingDisabled` se leería de Fundamentales aunque la
  // superficie sea Brillamos (misma clase de fuga que tenía el botón de carga masiva CSV).
  const rules = useRecognitionRules(undefined, programa);

  const ofrecida = vistas.data?.[clave] ?? USER_VIEWS_DEFAULT[clave];
  if (!ofrecida) return false;

  // La otra mitad, para las seis que tienen una regla propia. Las demás no la tienen y salen tal cual.
  switch (clave) {
    case 'enviarReconocimiento':
      return !(rules.data?.sendingDisabled ?? false);

    // Anti-Spoiler por campaña (2026-09-23, decisión 4) — el buscador de compañeros ya no depende
    // del Anti-Spoiler: siempre visible, y el servidor quita del muro ajeno lo protegido.

    default:
      return true;
  }
}

/**
 * URL del micrositio de un Fundamental, o `null` si no tiene.
 *
 * Devuelve `null` también con la cadena vacía: el servidor ya descarta las vacías al guardar, pero un
 * documento anterior a esa validación puede traerlas, y pintar un enlace a ninguna parte es peor que
 * no pintar ninguno.
 */
export function useMicrositioUrl(fundamentalId: string | undefined): string | null {
  const ouId = useCurrentUser()?.ouId;
  const vistas = useUserViews(ouId);
  if (!fundamentalId) return null;
  const url = vistas.data?.micrositeUrls?.[fundamentalId];
  return url && url.trim() ? url : null;
}
